// Project Detail — Works progress tracker. Each work item shows a % complete
// bar, status, start/end dates and linked project documents (scaffolding forms
// etc.). Autosaves the full list.
import { useEffect, useState } from "react";
import { Hammer, Plus, Trash2, Loader2, Save, FileText, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { SectionCard } from "@/components/SectionCard";

const STATUSES = ["Not started", "In progress", "On hold", "Complete"];
const STATUS_COLOR = { "Not started": "#9A968F", "In progress": "#2563EB", "On hold": "#C68A1C", "Complete": "#3F7D3A" };

export default function ProjectWorksPanel({ project, canEdit, onChanged }) {
    const navigate = useNavigate();
    const [rows, setRows] = useState(() => (project.works || []).map((w) => ({ ...w, document_ids: w.document_ids || [] })));
    const [docs, setDocs] = useState([]);
    const [busy, setBusy] = useState(false);
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        api.get("/documents", { params: { project_id: project.id } })
            .then((r) => setDocs(r.data || [])).catch(() => setDocs([]));
    }, [project.id]);

    const update = (i, patch) => { setRows((r) => r.map((x, idx) => (idx === i ? { ...x, ...patch } : x))); setDirty(true); };
    const add = () => { setRows((r) => [...r, { name: "", percent_complete: 0, status: "Not started", start_date: "", end_date: "", document_ids: [] }]); setDirty(true); };
    const remove = (i) => { setRows((r) => r.filter((_, idx) => idx !== i)); setDirty(true); };

    async function save() {
        const clean = rows.filter((r) => (r.name || "").trim()).map((r) => ({
            ...r, percent_complete: Math.max(0, Math.min(100, parseInt(r.percent_complete || 0, 10) || 0)),
            start_date: r.start_date || null, end_date: r.end_date || null,
        }));
        setBusy(true);
        try {
            const { data } = await api.put(`/projects/${project.id}/works`, { works: clean });
            setRows((data.works || []).map((w) => ({ ...w, document_ids: w.document_ids || [] })));
            setDirty(false);
            toast.success("Works saved");
            onChanged?.();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save"); }
        finally { setBusy(false); }
    }

    const avg = rows.length ? Math.round(rows.reduce((s, r) => s + (parseInt(r.percent_complete || 0, 10) || 0), 0) / rows.length) : 0;

    return (
        <SectionCard testId="project-works-panel" id={`works-${project.id}`} icon={Hammer}
            title="Works" count={`${rows.length} · avg ${avg}%`}
            actions={canEdit && (
                    <div className="flex items-center gap-2">
                        <button data-testid="work-add" onClick={add}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-[8px] text-[11.5px] font-semibold border bg-white hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA", color: "#2D2D2D" }}>
                            <Plus className="w-3.5 h-3.5" /> Add work
                        </button>
                        {dirty && (
                            <button data-testid="work-save" onClick={save} disabled={busy}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-[8px] text-[11.5px] font-semibold text-white disabled:opacity-60" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
                            </button>
                        )}
                    </div>
            )}>

            {rows.length === 0 ? (
                <div className="py-5 text-center text-[12.5px] italic" style={{ color: "#9A968F" }}>No works recorded.{canEdit ? " Add one above." : ""}</div>
            ) : (
                <div className="space-y-3">
                    {rows.map((r, i) => (
                        <div key={r.id || i} data-testid={`work-row-${i}`} className="rounded-[10px] border p-3 space-y-2.5" style={{ borderColor: "#EFEAE3", background: "#FCFBF9" }}>
                            <div className="flex items-center gap-2">
                                <input data-testid={`work-name-${i}`} value={r.name || ""} disabled={!canEdit} onChange={(e) => update(i, { name: e.target.value })}
                                    placeholder="Work name (e.g. Scaffolding)" className="flex-1 px-2.5 py-1.5 border rounded-lg text-[12.5px] font-semibold outline-none bg-white" style={{ borderColor: "#E5E0DA" }} />
                                <select data-testid={`work-status-${i}`} value={r.status || "Not started"} disabled={!canEdit} onChange={(e) => update(i, { status: e.target.value })}
                                    className="px-2 py-1.5 border rounded-lg text-[11.5px] font-semibold outline-none bg-white" style={{ borderColor: "#E5E0DA", color: STATUS_COLOR[r.status] }}>
                                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                                {canEdit && <button data-testid={`work-remove-${i}`} onClick={() => remove(i)} className="w-8 h-8 grid place-items-center rounded hover:bg-[#FBE8E5]" style={{ color: "#B33A2E" }}><Trash2 className="w-4 h-4" /></button>}
                            </div>

                            {/* progress */}
                            <div className="flex items-center gap-3">
                                <input type="range" data-testid={`work-progress-${i}`} min={0} max={100} step={5} value={r.percent_complete || 0} disabled={!canEdit}
                                    onChange={(e) => update(i, { percent_complete: parseInt(e.target.value, 10) })} className="flex-1 accent-[var(--brand-accent,#C75A1B)]" />
                                <span className="text-[12px] font-mono font-bold w-11 text-right" style={{ color: "#1F1F1F" }}>{r.percent_complete || 0}%</span>
                            </div>
                            <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "#EFEAE3" }}>
                                <div className="h-full rounded-full" style={{ width: `${r.percent_complete || 0}%`, background: STATUS_COLOR[r.status] || "var(--brand-accent,#C75A1B)" }} />
                            </div>

                            <div className="grid grid-cols-2 gap-2">
                                <label className="text-[10.5px] font-semibold" style={{ color: "#64718a" }}>Start
                                    <input type="date" data-testid={`work-start-${i}`} value={(r.start_date || "").slice(0, 10)} disabled={!canEdit} onChange={(e) => update(i, { start_date: e.target.value })}
                                        className="w-full mt-0.5 px-2 py-1.5 border rounded-lg text-[12px] outline-none bg-white" style={{ borderColor: "#E5E0DA" }} />
                                </label>
                                <label className="text-[10.5px] font-semibold" style={{ color: "#64718a" }}>End
                                    <input type="date" data-testid={`work-end-${i}`} value={(r.end_date || "").slice(0, 10)} disabled={!canEdit} onChange={(e) => update(i, { end_date: e.target.value })}
                                        className="w-full mt-0.5 px-2 py-1.5 border rounded-lg text-[12px] outline-none bg-white" style={{ borderColor: "#E5E0DA" }} />
                                </label>
                            </div>

                            {/* linked documents */}
                            <div>
                                <div className="text-[10.5px] font-semibold mb-1 flex items-center gap-1" style={{ color: "#64718a" }}><FileText className="w-3 h-3" /> Linked documents ({(r.document_ids || []).length})</div>
                                {(r.document_ids || []).length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 mb-1.5">
                                        {(r.document_ids || []).map((did) => {
                                            const d = docs.find((x) => x.id === did);
                                            return (
                                                <span key={did} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border" style={{ borderColor: "#E5E0DA", background: "#fff" }}>
                                                    {d?.title || d?.name || "Document"}
                                                    {d && <button onClick={() => navigate(`/app/documents?open=${did}`)} className="hover:text-[var(--brand-accent,#C75A1B)]"><ExternalLink className="w-3 h-3" /></button>}
                                                    {canEdit && <button data-testid={`work-doc-unlink-${i}-${did}`} onClick={() => update(i, { document_ids: (r.document_ids || []).filter((x) => x !== did) })} className="hover:text-[#B33A2E]">×</button>}
                                                </span>
                                            );
                                        })}
                                    </div>
                                )}
                                {canEdit && docs.length > 0 && (
                                    <select data-testid={`work-doc-link-${i}`} value="" onChange={(e) => { if (e.target.value) update(i, { document_ids: [...new Set([...(r.document_ids || []), e.target.value])] }); }}
                                        className="w-full px-2 py-1.5 border rounded-lg text-[11.5px] outline-none bg-white" style={{ borderColor: "#E5E0DA" }}>
                                        <option value="">+ Link a project document…</option>
                                        {docs.filter((d) => !(r.document_ids || []).includes(d.id)).map((d) => <option key={d.id} value={d.id}>{d.title || d.name}</option>)}
                                    </select>
                                )}
                                {canEdit && docs.length === 0 && <div className="text-[10.5px] italic" style={{ color: "#9A968F" }}>Upload documents to this project (Documents tab) to link them here.</div>}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </SectionCard>
    );
}
